import { markPropertyRateSellingPrice } from 'lib/channelMarkup/channelMarkup'

type PropertyRateUpgradeItem = App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem

interface AcceptedPricing {
  /** Bedbank's new standard total for the whole group. Absent when that figure didn't move. */
  price?: number
  /** Bedbank's new member total for the whole group. Absent when that figure didn't move. */
  luxPlusPrice?: number
  /** Unlike the prices, zero is a legitimate new amount. */
  propertyFees?: number
  /** How many items share the totals above, since bedbank only reports the group's total. */
  itemCount: number
}

/**
 * Bedbank quotes un-marked prices, so the new figure is the BASE price and the marked selling price
 * is re-derived from it with the markup the quote was mapped with. Writing bedbank's figure straight
 * into `sellingPrice` would leave the pair drifting: the upgrade discount reads the base and would
 * be computed against the pre-reprice price, and the display would lose its markup.
 */
function repriceTotals(
  totals: App.PropertyRateTotals,
  groupPrice: number | undefined,
  perItemFees: number | undefined,
  itemCount: number,
): App.PropertyRateTotals {
  const baseSellingPrice = groupPrice === undefined ? undefined : groupPrice / itemCount
  return {
    ...totals,
    ...(baseSellingPrice !== undefined && {
      baseSellingPrice,
      sellingPrice: markPropertyRateSellingPrice(baseSellingPrice, totals.channelMarkup),
    }),
    ...(perItemFees !== undefined && { propertyFees: perItemFees }),
  }
}

/**
 * Apply an accepted price change to the source quote saved by a removed loyalty package upgrade.
 *
 * The checkout item prices that source rate from `sourcePricing`, not current offer state. Updating
 * only offer state would make the next enquiry repeat the price bedbank refused. Replace only the
 * figures bedbank returned and preserve the rest of the saved quote.
 *
 * Leave an applied loyalty package upgrade unchanged because it books the target at the source price.
 */
function buildRepricedSourceItem(item: PropertyRateUpgradeItem, pricing: AcceptedPricing): PropertyRateUpgradeItem {
  const upgrade = item.loyaltyPropertyRateUpgrade
  if (!upgrade || upgrade.applied !== false) return item

  const { price, luxPlusPrice, propertyFees, itemCount } = pricing
  if (itemCount < 1) return item
  if (price === undefined && luxPlusPrice === undefined && propertyFees === undefined) return item

  // Bedbank reports one total for the group without a per-room split, matching the offer-state
  // update's existing even-division limitation.
  const perItemFees = propertyFees === undefined ? undefined : propertyFees / itemCount
  const { totals, luxPlusTotals } = upgrade.sourcePricing

  return {
    ...item,
    loyaltyPropertyRateUpgrade: {
      ...upgrade,
      sourcePricing: {
        totals: repriceTotals(totals, price, perItemFees, itemCount),
        ...(luxPlusTotals && {
          luxPlusTotals: repriceTotals(luxPlusTotals, luxPlusPrice, perItemFees, itemCount),
        }),
      },
    },
  }
}

export default buildRepricedSourceItem
